/**
 * @file This file manages the global state of the application,
 * including data fetched from Firebase, UI state, temporary form data,
 * and functions for loading/saving view settings to local storage.
 */

import {
    BOARD_LAYOUTS,
    BOARD_CONTRACT_VIEW_KEYS,
    createDefaultBoardViewSettings,
    getBoardRegistryEntry,
} from "../../shared/board/boardRegistry.js";

const MOBILE_BOARD_LAYOUT_MEDIA = "(max-width: 767px)";

// --- Global Data Store ---

/**
 * Holds all the primary data fetched from Firestore collections.
 * @property {Array<object>} patients - Array of patient objects.
 * @property {Array<object>} projects - Array of project objects.
 * @property {Array<object>} assets - Array of asset objects.
 * @property {Array<object>} labs - Array of Labs board objects.
 * @property {Array<object>} maintenance - Array of Maintenance board objects.
 * @property {Array<object>} recalls - Route-loaded active Recalls rows.
 * @property {Array<object>} closedRecalls - Route-loaded closed Recalls rows.
 * @property {Array<object>} recallDrafts - Route-loaded shared Recalls drafts.
 * @property {Array<object>} tasks - Array of task objects (filtered for current user).
 * @property {Array<object>} users - Array of user profile objects.
 * @property {object} config - Configuration object from Firestore (statuses, priorities, etc.).
 * @property {Array<object>} checklistTemplates - Array of checklist template objects.
 */
export const appData = {
    patients: [],
    projects: [],
    assets: [],
    labs: [],
    maintenance: [],
    recalls: [],
    closedRecalls: [],
    recallDrafts: [],
    tasks: [],
    users: [],
    userInvites: [],
    teams: [],
    config: {},
    checklistTemplates: [], // Global cache for checklist templates
    notifications: [],
    drugInventory: [],
    drugTransactions: [],
    drugLedger: [],
    drugAudits: [],
    drugRoles: [],
    activeAudit: null,
};

/**
 * Holds the current UI and user state.
 * @property {string} currentView - The key of the currently active view (e.g., 'tasks', 'patients').
 * @property {object | null} currentUser - The Firebase Auth user object.
 * @property {object | null} currentUserProfile - The user's profile data from the 'users' collection.
 * @property {string | null} selectedItemId - The ID of the item currently being viewed in the details view.
 * @property {object} viewSettings - User preferences for each view (layout, grouping, sorting, filters, columns), loaded from localStorage.
 * @property {boolean} showWIP - Flag to toggle the detailed "Work In Progress" Kanban view.
 * @property {string | null} originatingView - Tracks which main view the user came from when entering the details view.
 * @property {Array<object>} detailNavigationStack - Tracks prior detail items when drilling from one detail view into another.
 * @property {object} detailNoteDraft - Unsaved note composer draft for the active detail item.
 */
export const state = {
    currentView: 'tasks',
    calendarView: null, // null | 'day/YYYY-MM-DD'
    // --- Drugs View State ---
    drugsView: null, // 'actions' | 'stock' | 'history' | 'audit' - Defaults to null (calculated in renderDrugs.js)
    drugHistoryPagination: { lastDoc: null, hasMore: true, minVisibleItems: 50 },
    drugAuditPagination: { lastDoc: null, hasMore: true },
    drugAuditHistory: [],
    drugAuditHistoryPagination: { lastDoc: null, hasMore: true },
    drugHistoryFilter: 'all', // Filter for history table: 'all' | 'use' | 'relocate' | 'restock' | 'correction' | 'loss'
    currentUser: null,
    currentUserProfile: null,
    selectedItemId: null,
    selectedItemType: null,
    viewSettings: {},
    showWIP: false,
    // Mutable closed-item pagination state stays here until a pagination
    // ownership split moves runtime cursors out of the global state surface.
    closedItemsPagination: {
        tasks: {
            ownerLastDoc: null,
            assignedLastDoc: null,
            ownerHasMore: false,
            assignedHasMore: false,
            hasMore: false,
            hasLoadedMore: false,
            extraItems: [],
        },
        patients: { lastDoc: null, hasMore: false, hasLoadedMore: false, extraItems: [] },
        projects: { lastDoc: null, hasMore: false, hasLoadedMore: false, extraItems: [] },
        assets: { lastDoc: null, hasMore: false, hasLoadedMore: false, extraItems: [] },
        labs: { lastDoc: null, hasMore: false, hasLoadedMore: false, extraItems: [] },
        maintenance: { lastDoc: null, hasMore: false, hasLoadedMore: false, extraItems: [] },
    },
    showClosed: false,
    originatingView: null,
    detailNavigationStack: [],
    settingsReturnContext: null,
    isNotificationSettingsOpen: false,
    hasUnsavedChanges: false,
    detailNoteDraft: { itemId: null, text: '' },
    drugHistoryFocusId: null,
};

/**
 * Temporarily holds a deep copy of the `appData.config` for editing in the settings view.
 * This allows users to cancel changes without affecting the live config.
 * @type {object | null}
 */
export let tempConfigData = null;

/**
 * Immutable-at-entry configuration snapshot used to identify Settings sections
 * intentionally changed by the current edit session.
 * @type {object | null}
 */
export let tempConfigBaselineData = null;

/**
 * Temporarily holds a deep copy of the users array for editing in the settings view.
 * @type {Array<object> | null}
 */
export let tempUsersData = null;

/**
 * Temporarily holds a deep copy of the teams array for editing in the settings view.
 * @type {Array<object> | null}
 */
export let tempTeamsData = null;

/**
 * Holds the currently opened user deactivation draft in Settings.
 * @type {object | null}
 */
export let tempUserDeactivationDraft = null;

/**
 * Holds active deactivation draft summaries for the Users settings tab.
 * @type {Array<object>}
 */
export let deactivationDraftSummaries = [];

/**
 * Stores unsubscribe functions for Firestore listeners to detach them on logout.
 * Managed primarily by app data listeners.
 * @type {Array<Function>}
 */
export const activeListeners = [];

/**
 * Flag to ensure data listeners are only attached once after user profile loads.
 * Managed primarily by app data listeners.
 * @type {boolean}
 */
export let dataListenersAttached = false;

/**
 * Holds data specifically for the details view, primarily tasks linked to the parent item.
 * @property {Array<object>} tasks - Array of task objects linked to the currently viewed parent item.
 */
export const appDataDetails = {
    tasks: [],
    notes: [],
};

/**
 * Stores unsubscribe functions for Firestore listeners specific to the details view.
 * Managed primarily by `app/routing/navigationController.js`.
 * @type {Array<Function>}
 */
export const detailViewListeners = [];

// --- State Update Functions --- 

function insertColumnBefore(columns, columnKey, anchorKeys = []) {
    if (!Array.isArray(columns) || columns.includes(columnKey)) {return columns || [];}

    const nextColumns = [...columns];
    const anchorIndex = nextColumns.findIndex((key) => anchorKeys.includes(key));

    if (anchorIndex === -1) {
        nextColumns.push(columnKey);
    } else {
        nextColumns.splice(anchorIndex, 0, columnKey);
    }

    return nextColumns;
}

function migrateTypeColumnDefaults(settings) {
    if (!settings) {return settings;}

    return {
        ...settings,
        patients: {
            ...settings.patients,
            visibleColumns: insertColumnBefore(
                settings.patients?.visibleColumns || [],
                "types",
                ["priority", "status"],
            ),
        },
        projects: {
            ...settings.projects,
            visibleColumns: insertColumnBefore(
                settings.projects?.visibleColumns || [],
                "types",
                ["priority", "status"],
            ),
        },
        assets: {
            ...settings.assets,
            visibleColumns: insertColumnBefore(
                settings.assets?.visibleColumns || [],
                "types",
                ["equipment_status"],
            ),
        },
        tasks: {
            ...settings.tasks,
            visibleColumns: insertColumnBefore(
                settings.tasks?.visibleColumns || [],
                "types",
                ["priority", "status"],
            ),
        },
    };
}

const BOARD_VIEW_STORAGE_KEY = "managementAppViewSettings_v2";
const LEGACY_BOARD_VIEW_STORAGE_KEY = "managementAppViewSettings_v1";

function createDefaultBoardSettings() {
    return createDefaultBoardViewSettings();
}

function createDefaultViewSettings() {
    return {
        ...createDefaultBoardSettings(),
        drugsHistory: {
            style: "table",
            filters: {},
            sortBy: "transactionDate",
            sortDirection: "desc",
            visibleColumns: [
                "transactionDate",
                "type",
                "drug",
                "location",
                "quantity",
                "balance",
                "notes",
                "performedBy",
                "status",
                "actions",
            ],
            columnWidths: { notes: 200, actions: 80 },
        },
        notificationsPinned: false,
        notificationSort: { date: "recent", group: "none" },
    };
}

function mapLegacyStyleToBoardSettings(defaults, legacyStyle) {
    const nextSettings = { ...defaults };

    if (legacyStyle === "table") {
        nextSettings.layout = BOARD_LAYOUTS.table;
        return nextSettings;
    }

    nextSettings.layout = BOARD_LAYOUTS.board;

    if (legacyStyle === "kanban-status") {
        nextSettings.boardGroupBy = "status";
    } else if (legacyStyle === "kanban-date") {
        nextSettings.boardGroupBy = "date";
    }

    return nextSettings;
}

function sanitizeBoardSettings(viewKey, savedSettings, defaultSettings) {
    const legacyMappedSettings = savedSettings?.style ?
        mapLegacyStyleToBoardSettings(defaultSettings, savedSettings.style) :
        defaultSettings;
    const mergedSettings = {
        ...legacyMappedSettings,
        ...(savedSettings || {}),
        columnWidths: {
            ...legacyMappedSettings.columnWidths,
            ...(savedSettings?.columnWidths || {}),
        },
    };

    const layout = mergedSettings.layout === BOARD_LAYOUTS.table ?
        BOARD_LAYOUTS.table : BOARD_LAYOUTS.board;
    const supportedGroupKeys = getBoardRegistryEntry(viewKey)
        ?.grouping?.supportedGroupKeys || [];
    const boardGroupBy = supportedGroupKeys.includes(mergedSettings.boardGroupBy) ?
        mergedSettings.boardGroupBy : defaultSettings.boardGroupBy;
    const tableGroupBy = supportedGroupKeys.includes(mergedSettings.tableGroupBy) ?
        mergedSettings.tableGroupBy : defaultSettings.tableGroupBy;
    const boardDateGroupingMode = ["daily", "weekly", "monthly"]
        .includes(mergedSettings.boardDateGroupingMode) ?
        mergedSettings.boardDateGroupingMode :
        defaultSettings.boardDateGroupingMode;
    const tableDateGroupingMode = ["daily", "weekly", "monthly"]
        .includes(mergedSettings.tableDateGroupingMode) ?
        mergedSettings.tableDateGroupingMode :
        defaultSettings.tableDateGroupingMode;

    return {
        ...mergedSettings,
        layout,
        boardGroupBy,
        tableGroupBy,
        boardDateGroupingMode,
        tableDateGroupingMode,
        filters: mergedSettings.filters || {},
        searchDraft: typeof mergedSettings.searchDraft === "string" ?
            mergedSettings.searchDraft : "",
        searchQuery: typeof mergedSettings.searchQuery === "string" ?
            mergedSettings.searchQuery : "",
        visibleColumns: Array.isArray(mergedSettings.visibleColumns) ?
            mergedSettings.visibleColumns :
            [...defaultSettings.visibleColumns],
    };
}

/**
 * Returns persisted runtime settings for a board view.
 * @param {string} viewKey Board view key.
 * @returns {object | null} Board view settings.
 */
export function getBoardViewSettings(viewKey) {
    return state.viewSettings?.[viewKey] || null;
}

/**
 * Returns the active layout for a board view.
 * @param {string} viewKey Board view key.
 * @returns {string} Board layout key.
 */
export function getViewLayout(viewKey) {
    return getBoardViewSettings(viewKey)?.layout || BOARD_LAYOUTS.board;
}

/**
 * Returns the viewport-effective layout for a board view without mutating the
 * saved layout preference.
 * @param {string} viewKey Board view key.
 * @returns {string} Effective board layout key.
 */
export function getEffectiveViewLayout(viewKey) {
    const savedLayout = getViewLayout(viewKey);
    const isParentBoard = BOARD_CONTRACT_VIEW_KEYS.includes(viewKey);
    const isMobile = window.matchMedia?.(MOBILE_BOARD_LAYOUT_MEDIA)
        ?.matches === true;

    if (isParentBoard && isMobile) {
        return BOARD_LAYOUTS.board;
    }

    return savedLayout;
}

/**
 * Converts structured board layout settings to the legacy style value.
 * @param {string} viewKey Board view key.
 * @returns {string} Legacy style value.
 */
export function getLegacyStyleValue(viewKey) {
    const settings = getBoardViewSettings(viewKey);

    if (!settings) {return "table";}
    if (settings.layout === BOARD_LAYOUTS.table) {return "table";}
    if (settings.boardGroupBy === "date") {return "kanban-date";}
    if (settings.boardGroupBy === "status") {return "kanban-status";}

    return "kanban-status";
}

/**
 * Applies a legacy style selection to structured board layout settings.
 * @param {string} viewKey Board view key.
 * @param {string} selectedStyle Legacy style value.
 * @returns {void}
 */
export function applyLegacyStyleSelection(viewKey, selectedStyle) {
    const settings = getBoardViewSettings(viewKey);
    if (!settings) {return;}

    if (selectedStyle === "table") {
        settings.layout = BOARD_LAYOUTS.table;
        return;
    }

    settings.layout = BOARD_LAYOUTS.board;

    if (selectedStyle === "kanban-date") {
        settings.boardGroupBy = "date";
        return;
    }

    settings.boardGroupBy = "status";
}

function getPersistedViewSettings() {
    const defaultSettings = createDefaultViewSettings();
    const savedSettingsString = localStorage.getItem(BOARD_VIEW_STORAGE_KEY);

    if (!savedSettingsString) {
        localStorage.removeItem(LEGACY_BOARD_VIEW_STORAGE_KEY);
        return defaultSettings;
    }

    const savedSettings = JSON.parse(savedSettingsString);

    const boardSettings = BOARD_CONTRACT_VIEW_KEYS.reduce((settings, viewKey) => {
        settings[viewKey] = sanitizeBoardSettings(
            viewKey,
            savedSettings[viewKey],
            defaultSettings[viewKey],
        );
        return settings;
    }, {});

    return {
        ...boardSettings,
        drugsHistory: {
            ...defaultSettings.drugsHistory,
            ...(savedSettings.drugsHistory || {}),
            columnWidths: {
                ...defaultSettings.drugsHistory.columnWidths,
                ...(savedSettings.drugsHistory?.columnWidths || {}),
            },
        },
        notificationSort: {
            ...defaultSettings.notificationSort,
            ...(savedSettings.notificationSort || {}),
        },
        notificationsPinned:
            savedSettings.notificationsPinned ??
            defaultSettings.notificationsPinned,
    };
}

function getSanitizedViewSettingsForSave() {
    const settingsToSave = structuredClone(state.viewSettings || {});

    BOARD_CONTRACT_VIEW_KEYS.forEach((viewKey) => {
        const boardSettings = settingsToSave[viewKey];

        if (!boardSettings) {return;}

        delete boardSettings.style;
        delete boardSettings.filters;
        delete boardSettings.searchDraft;
        delete boardSettings.searchQuery;
        delete boardSettings.sortBy;
        delete boardSettings.sortDirection;
    });

    return settingsToSave;
}

/**
 * Updates the global appData.tasks array safely using splice.
 * @param {Array<object>} newTasksArray - The new array of task objects.
 */
export function updateGlobalTasks(newTasksArray) {
    if (!Array.isArray(newTasksArray)) {
        console.error("updateGlobalTasks received non-array:", newTasksArray);
        return;
    }
    // Use splice to modify the existing array in place
    appData.tasks.splice(0, appData.tasks.length, ...newTasksArray);
    // console.log(`Updated global tasks via updateGlobalTasks. Count: ${appData.tasks.length}`);
}

/**
 * Sets the status of whether the main data listeners have been attached.
 * @param {boolean} status - True if attached, false otherwise.
 */
export function setDataListenersAttachedStatus(status) {
    dataListenersAttached = Boolean(status); // Ensure it's explicitly boolean
    // console.log(`dataListenersAttached set to: ${dataListenersAttached}`); // Optional debug log
}

/**
 * Clears the temporary configuration data used in the settings view.
 */
export function clearTempConfigData() {
    tempConfigData = null;
    tempConfigBaselineData = null;
    // console.log("Cleared tempConfigData."); // Optional debug log
}

/**
 * Stores the currently opened deactivation draft.
 * @param {object | null} draft - Draft payload from backend.
 */
export function setTempUserDeactivationDraft(draft) {
    tempUserDeactivationDraft = draft ? structuredClone(draft) : null;
}

/**
 * Clears the currently opened deactivation draft.
 */
export function clearTempUserDeactivationDraft() {
    tempUserDeactivationDraft = null;
}

/**
 * Stores active deactivation draft summaries for the Users tab.
 * @param {Array<object>} drafts - Active draft summary array.
 */
export function setDeactivationDraftSummaries(drafts) {
    deactivationDraftSummaries = Array.isArray(drafts) ?
        structuredClone(drafts) : [];
}

/**
 * Clears loaded deactivation draft summaries.
 */
export function clearDeactivationDraftSummaries() {
    deactivationDraftSummaries = [];
}

/**
 * Creates a deep copy of the main config and stores it in tempConfigData for editing.
 * @param {object} mainConfig - The main application configuration object (appData.config).
 */
export function setTempConfigData(mainConfig) {
    if (!mainConfig) {
        console.error("setTempConfigData: Main config is missing.");
        tempConfigData = null; // Ensure it's null if copy fails
        tempConfigBaselineData = null;
        return;
    }
    try {
        tempConfigData = structuredClone(mainConfig);
        tempConfigBaselineData = structuredClone(mainConfig);
    } catch (e) {
        console.error("Failed to deep copy config for settings:", e);
        tempConfigData = null;
        tempConfigBaselineData = null;
    }
}

/**
 * Advances the Settings config comparison baseline after a successful commit
 * without replacing the active editable draft.
 * @param {object} mainConfig Persisted runtime configuration shape.
 * @returns {void}
 */
export function rebaseTempConfigBaselineData(mainConfig) {
    if (!mainConfig) {return;}
    try {
        tempConfigBaselineData = structuredClone(mainConfig);
    } catch (error) {
        console.error("Failed to rebase Settings config baseline:", error);
    }
}

/**
 * Stores a cloned Users settings draft.
 * @param {Array<object>} usersArray User records to edit.
 * @returns {void}
 */
export function setTempUsersData(usersArray) {
    if (!usersArray) {
        tempUsersData = [];
        return;
    }
    try {
        tempUsersData = structuredClone(usersArray);
    } catch (e) {
        console.error("Failed to deep copy users for settings:", e);
        tempUsersData = [];
    }
}

/**
 * Clears the Users settings draft state.
 * @returns {void}
 */
export function clearTempUsersData() {
    tempUsersData = null;
}

/**
 * Stores a cloned Teams settings draft.
 * @param {Array<object>} teamsArray Team records to edit.
 * @returns {void}
 */
export function setTempTeamsData(teamsArray) {
    if (!teamsArray) {
        tempTeamsData = [];
        return;
    }
    try {
        tempTeamsData = structuredClone(teamsArray);
    } catch (e) {
        console.error("Failed to deep copy teams for settings:", e);
        tempTeamsData = [];
    }
}

/**
 * Clears the Teams settings draft state.
 * @returns {void}
 */
export function clearTempTeamsData() {
    tempTeamsData = null;
}

// --- Local Storage Management for View Settings ---

/**
 * Loads view settings from local storage into the global `state.viewSettings`.
 * If no settings are found or they are invalid, applies default settings.
 */
export function loadViewSettings() {
    try {
        state.viewSettings = migrateTypeColumnDefaults(getPersistedViewSettings());
    } catch (e) {
        console.error("Failed to parse saved view settings, using defaults:", e);
        state.viewSettings = migrateTypeColumnDefaults(createDefaultViewSettings());
        localStorage.removeItem(BOARD_VIEW_STORAGE_KEY);
    }

    state.showWIP = false; // Initialize showWIP separately
}

/**
 * Saves the current `state.viewSettings` object to local storage.
 */
export function saveViewSettings() {
    try {
        localStorage.setItem(
            BOARD_VIEW_STORAGE_KEY,
            JSON.stringify(getSanitizedViewSettingsForSave()),
        );
    } catch (e) {
        console.error("Failed to save view settings to local storage:", e);
    }
}

/**
 * Clears transient search, filter, and sort state for board views.
 * @param {Array<string>} [viewKeys=BOARD_CONTRACT_VIEW_KEYS] Board view keys to clear.
 * @returns {void}
 */
export function clearBoardRuntimeState(viewKeys = BOARD_CONTRACT_VIEW_KEYS) {
    (viewKeys || []).forEach((viewKey) => {
        const boardSettings = state.viewSettings?.[viewKey];
        if (!boardSettings) {return;}
        boardSettings.searchDraft = "";
        boardSettings.searchQuery = "";
        boardSettings.filters = {};
        boardSettings.sortBy = null;
        boardSettings.sortDirection = "asc";
    });
}
